import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Minus, Calendar, Download, 
  Droplets, Activity, BarChart3, RefreshCw, ArrowUp, ArrowDown
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIME_RANGES = [
  { value: '7', label: '7 Days', labelHi: '7 दिन' },
  { value: '30', label: '30 Days', labelHi: '30 दिन' },
  { value: '90', label: '90 Days', labelHi: '90 दिन' },
  { value: '365', label: '1 Year', labelHi: '1 वर्ष' },
];

const METRICS = [
  { value: 'nrw', label: 'NRW %', labelHi: 'NRW %', color: '#ef4444' },
  { value: 'supply', label: 'Supply (MLD)', labelHi: 'आपूर्ति (MLD)', color: '#3b82f6' },
  { value: 'demand', label: 'Demand (MLD)', labelHi: 'मांग (MLD)', color: '#22c55e' },
];

const QUALITY_PARAMS = [
  { value: 'ph', label: 'pH Level', labelHi: 'pH स्तर', unit: '', ideal: '6.5-8.5' },
  { value: 'turbidity', label: 'Turbidity', labelHi: 'मैलापन', unit: 'NTU', ideal: '<4' },
  { value: 'chlorine', label: 'Chlorine', labelHi: 'क्लोरीन', unit: 'mg/L', ideal: '0.2-1.0' },
  { value: 'tds', label: 'TDS', labelHi: 'TDS', unit: 'ppm', ideal: '<500' },
];

const TrendIndicator = ({ trend, trendDirection }) => {
  const getIcon = () => {
    if (trendDirection === 'increasing') return <ArrowUp className="w-4 h-4" />;
    if (trendDirection === 'decreasing') return <ArrowDown className="w-4 h-4" />;
    return <Minus className="w-4 h-4" />;
  };

  const getColor = () => {
    if (trendDirection === 'increasing') return 'text-red-500';
    if (trendDirection === 'decreasing') return 'text-green-500';
    return 'text-gray-500';
  };

  return (
    <div className={`flex items-center gap-1 ${getColor()}`}>
      {getIcon()}
      <span className="text-sm font-medium">{Math.abs(trend).toFixed(1)}%</span>
    </div>
  );
};

const SummaryCard = ({ title, value, unit, trend, trendDirection, minMax }) => (
  <div className="bg-muted/50 rounded-lg p-3 sm:p-4">
    <p className="text-xs sm:text-sm text-muted-foreground mb-1">{title}</p>
    <div className="flex items-end justify-between">
      <div>
        <p className="text-lg sm:text-2xl font-bold">{value}{unit && <span className="text-sm ml-1">{unit}</span>}</p>
        {minMax && (
          <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
            Min: {minMax.min} | Max: {minMax.max}
          </p>
        )}
      </div>
      {trend !== undefined && <TrendIndicator trend={trend} trendDirection={trendDirection} />}
    </div>
  </div>
);

export const HistoricalData = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('30');
  const [selectedMetric, setSelectedMetric] = useState('nrw');
  const [selectedQualityParam, setSelectedQualityParam] = useState('ph');
  const [isLoading, setIsLoading] = useState(false);
  
  // Data states
  const [nrwData, setNrwData] = useState({ data: [], summary: {} });
  const [supplyData, setSupplyData] = useState({ data: [], summary: {} });
  const [demandData, setDemandData] = useState({ data: [], summary: {} });
  const [qualityData, setQualityData] = useState({ data: [], summary: {} });
  const [comparisonData, setComparisonData] = useState({});

  useEffect(() => {
    loadHistoricalData();
  }, [timeRange, selectedMetric, selectedQualityParam]);

  const loadHistoricalData = async () => {
    setIsLoading(true);
    try {
      // Load NRW data
      const nrwRes = await fetch(`${API_URL}/api/historical/nrw?days=${timeRange}`);
      const nrw = await nrwRes.json();
      if (nrw.success) setNrwData({ data: nrw.data, summary: nrw.summary });

      // Load supply data
      const supplyRes = await fetch(`${API_URL}/api/historical/supply?days=${timeRange}`);
      const supply = await supplyRes.json();
      if (supply.success) setSupplyData({ data: supply.data, summary: supply.summary });

      // Load demand data
      const demandRes = await fetch(`${API_URL}/api/historical/demand?days=${timeRange}`);
      const demand = await demandRes.json();
      if (demand.success) setDemandData({ data: demand.data, summary: demand.summary });

      // Load quality data
      const qualityRes = await fetch(`${API_URL}/api/historical/quality?days=${timeRange}&parameter=${selectedQualityParam}`);
      const quality = await qualityRes.json();
      if (quality.success) setQualityData({ data: quality.data, summary: quality.summary });

      // Load comparison data
      const compRes = await fetch(`${API_URL}/api/historical/comparison?days=${timeRange}&metric=${selectedMetric}`);
      const comp = await compRes.json();
      if (comp.success) setComparisonData(comp.villages || {});

    } catch (err) {
      console.error('Failed to load historical data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Combine supply and demand for overview chart.
  // NRW is a portion of supply, so it is plotted as a VOLUME (MLD) on the same
  // axis as Supply/Demand. This guarantees the NRW line stays below both lines.
  const combinedData = supplyData.data.map((item, idx) => {
    const supply = item.value;
    const nrwPct = nrwData.data[idx]?.value || 0;
    return {
      date: item.date,
      supply,
      demand: demandData.data[idx]?.value || 0,
      nrw: parseFloat((supply * (nrwPct / 100)).toFixed(3)),
      nrwPct,
    };
  });

  // Prepare comparison chart data
  const comparisonChartData = nrwData.data.map((item, idx) => {
    const dataPoint = { date: item.date };
    Object.keys(comparisonData).forEach(village => {
      dataPoint[village] = comparisonData[village]?.data[idx]?.value || 0;
    });
    return dataPoint;
  });

  const villageColors = {
    Margao: '#ef4444',
    Colva: '#3b82f6',
    Verna: '#22c55e',
    Benaulim: '#f59e0b',
    Cortalim: '#8b5cf6',
  };

  const getLabel = (item, field) => language === 'hi' ? item[`${field}Hi`] || item[field] : item[field];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-1 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
            {language === 'hi' ? 'ऐतिहासिक डेटा' : 'Historical Data'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {language === 'hi' ? 'समय के साथ रुझान और पैटर्न का विश्लेषण करें' : 'Analyze trends and patterns over time'}
          </p>
        </div>
        
        {/* Time Range Selector */}
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32 sm:w-40" data-testid="time-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_RANGES.map(range => (
                <SelectItem key={range.value} value={range.value}>
                  {language === 'hi' ? range.labelHi : range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={loadHistoricalData}
            disabled={isLoading}
            data-testid="refresh-historical-btn"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 h-auto">
          <TabsTrigger value="overview" className="text-xs sm:text-sm py-2">
            {language === 'hi' ? 'अवलोकन' : 'Overview'}
          </TabsTrigger>
          <TabsTrigger value="nrw" className="text-xs sm:text-sm py-2">
            NRW
          </TabsTrigger>
          <TabsTrigger value="quality" className="text-xs sm:text-sm py-2">
            {language === 'hi' ? 'गुणवत्ता' : 'Quality'}
          </TabsTrigger>
          <TabsTrigger value="comparison" className="text-xs sm:text-sm py-2">
            {language === 'hi' ? 'तुलना' : 'Compare'}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <SummaryCard
              title={language === 'hi' ? 'औसत NRW' : 'Avg NRW'}
              value={nrwData.summary.average || 0}
              unit="%"
              trend={nrwData.summary.trend}
              trendDirection={nrwData.summary.trendDirection}
            />
            <SummaryCard
              title={language === 'hi' ? 'औसत आपूर्ति' : 'Avg Supply'}
              value={supplyData.summary.average || 0}
              unit=" MLD"
              trend={supplyData.summary.trend}
              trendDirection={supplyData.summary.trendDirection}
            />
            <SummaryCard
              title={language === 'hi' ? 'औसत मांग' : 'Avg Demand'}
              value={demandData.summary.average || 0}
              unit=" MLD"
              trend={demandData.summary.trend}
              trendDirection={demandData.summary.trendDirection}
            />
            <SummaryCard
              title={language === 'hi' ? 'नवीनतम NRW' : 'Latest NRW'}
              value={nrwData.summary.latest || 0}
              unit="%"
              minMax={{ min: nrwData.summary.min, max: nrwData.summary.max }}
            />
          </div>

          {/* Combined Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base sm:text-lg">
                {language === 'hi' ? 'आपूर्ति बनाम मांग बनाम NRW' : 'Supply vs Demand vs NRW'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 sm:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={combinedData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="date" 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={10}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getDate()}/${date.getMonth() + 1}`;
                      }}
                      interval={Math.floor(combinedData.length / 7)}
                    />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={10}
                      label={{ value: 'MLD', angle: -90, position: 'insideLeft', fontSize: 10 }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                      formatter={(value, name, props) => {
                        if (props?.dataKey === 'nrw') {
                          const pct = props?.payload?.nrwPct;
                          return [`${value} MLD (${pct}%)`, name];
                        }
                        return [`${value} MLD`, name];
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Area 
                      type="monotone" 
                      dataKey="supply" 
                      fill="#3b82f620" 
                      stroke="#3b82f6"
                      name={language === 'hi' ? 'आपूर्ति' : 'Supply'}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="demand" 
                      fill="#22c55e20" 
                      stroke="#22c55e"
                      name={language === 'hi' ? 'मांग' : 'Demand'}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="nrw" 
                      stroke="#ef4444"
                      strokeWidth={2}
                      dot={false}
                      name={language === 'hi' ? 'NRW हानि (MLD)' : 'NRW Loss (MLD)'}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* NRW Tab */}
        <TabsContent value="nrw" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <SummaryCard
              title={language === 'hi' ? 'औसत' : 'Average'}
              value={nrwData.summary.average || 0}
              unit="%"
              trend={nrwData.summary.trend}
              trendDirection={nrwData.summary.trendDirection}
            />
            <SummaryCard
              title={language === 'hi' ? 'न्यूनतम' : 'Minimum'}
              value={nrwData.summary.min || 0}
              unit="%"
            />
            <SummaryCard
              title={language === 'hi' ? 'अधिकतम' : 'Maximum'}
              value={nrwData.summary.max || 0}
              unit="%"
            />
            <SummaryCard
              title={language === 'hi' ? 'नवीनतम' : 'Latest'}
              value={nrwData.summary.latest || 0}
              unit="%"
            />
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base sm:text-lg">
                {language === 'hi' ? 'NRW रुझान' : 'NRW Trend'}
              </CardTitle>
              <CardDescription>
                {language === 'hi' ? 'लक्ष्य: 8% से कम' : 'Target: Below 8%'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 sm:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={nrwData.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="date" 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={10}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getDate()}/${date.getMonth() + 1}`;
                      }}
                      interval={Math.floor(nrwData.data.length / 7)}
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} domain={[0, 'auto']} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [`${value}%`, 'NRW']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#ef4444"
                      fill="#ef444420"
                      strokeWidth={2}
                    />
                    {/* Target line at 8% */}
                    <Line 
                      type="monotone"
                      dataKey={() => 8}
                      stroke="#22c55e"
                      strokeDasharray="5 5"
                      dot={false}
                      name={language === 'hi' ? 'लक्ष्य' : 'Target'}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quality Tab */}
        <TabsContent value="quality" className="space-y-4 mt-4">
          <div className="flex flex-wrap gap-2 mb-4">
            {QUALITY_PARAMS.map(param => (
              <Button
                key={param.value}
                variant={selectedQualityParam === param.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedQualityParam(param.value)}
                className="text-xs sm:text-sm"
              >
                {getLabel(param, 'label')}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <SummaryCard
              title={language === 'hi' ? 'औसत' : 'Average'}
              value={qualityData.summary.average || 0}
              unit={QUALITY_PARAMS.find(p => p.value === selectedQualityParam)?.unit}
            />
            <SummaryCard
              title={language === 'hi' ? 'न्यूनतम' : 'Minimum'}
              value={qualityData.summary.min || 0}
            />
            <SummaryCard
              title={language === 'hi' ? 'अधिकतम' : 'Maximum'}
              value={qualityData.summary.max || 0}
            />
            <SummaryCard
              title={language === 'hi' ? 'आदर्श सीमा' : 'Ideal Range'}
              value={QUALITY_PARAMS.find(p => p.value === selectedQualityParam)?.ideal || '-'}
            />
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base sm:text-lg">
                {getLabel(QUALITY_PARAMS.find(p => p.value === selectedQualityParam) || {}, 'label')} {language === 'hi' ? 'रुझान' : 'Trend'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 sm:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={qualityData.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="date" 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={10}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getDate()}/${date.getMonth() + 1}`;
                      }}
                      interval={Math.floor(qualityData.data.length / 7)}
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Comparison Tab */}
        <TabsContent value="comparison" className="space-y-4 mt-4">
          <div className="flex flex-wrap gap-2 mb-4">
            {METRICS.map(metric => (
              <Button
                key={metric.value}
                variant={selectedMetric === metric.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedMetric(metric.value)}
                className="text-xs sm:text-sm"
              >
                {getLabel(metric, 'label')}
              </Button>
            ))}
          </div>

          {/* Village comparison summary */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
            {Object.entries(comparisonData).map(([village, data]) => (
              <Card key={village} className="p-2 sm:p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: villageColors[village] }}
                  />
                  <span className="text-xs sm:text-sm font-medium truncate">{village}</span>
                </div>
                <p className="text-lg sm:text-xl font-bold">{data.summary?.average?.toFixed(1) || 0}</p>
                <TrendIndicator 
                  trend={data.summary?.trend || 0} 
                  trendDirection={data.summary?.trendDirection || 'stable'} 
                />
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base sm:text-lg">
                {language === 'hi' ? 'गांव तुलना' : 'Village Comparison'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 sm:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={comparisonChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="date" 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={10}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getDate()}/${date.getMonth() + 1}`;
                      }}
                      interval={Math.floor(comparisonChartData.length / 7)}
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    {Object.keys(villageColors).map(village => (
                      <Line 
                        key={village}
                        type="monotone" 
                        dataKey={village} 
                        stroke={villageColors[village]}
                        strokeWidth={2}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HistoricalData;
